import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import PeriodDropdown from '../../shared/periodDropdown.component';
import Select from '../../shared/reactSelect/select.component';
import HamburgerDropdown from './hamburgerDropdown.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import { listItemPropTypes } from '../../shared/propTypes';

import { modalDataSelector } from '../../shared/modals/modals.selectors';
import ModalNames from './modalNames';

import {
  periodOrCurrentSelector,
  reportingCurrencyCodeSelector,
  userSelector,
  isInCurrentPeriodSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import {
  displayZeroSelector,
  hideDecimalsSelector,
  negativeMinusSelector,
} from '../../userData/store/selectors';

import {
  fetchInitialData,
  selectGroupTypeForViewEffectiveTaxRatePage,
  selectGroupItemForViewEffectiveTaxRatePage,
  selectGroupBy,
  selectViewBy,
  selectCountryTaxType,
  selectStatutoryTaxRate,
  resetCountryTaxTypesAndRates,
  fetchReport,
  resetETRReport,
  showDeletingDriverModal,
  hideDeletingDriverModal,
  deleteCustomETRDriver,
  exportToExcel,
  toggleVisibility,
} from './store/actions';

import {
  selectSlGroupTypes,
  selectSlGroupItems,
  selectSlGroupType,
  selectSlGroupItem,
  groupByOptionsSelector,
  groupBySelector,
  viewBySelector,
  countryTaxTypesStatutoryTaxRatesSelector,
  countryTaxTypesSelector,
  reportDataSelector,
  detailedCountryTaxTypesDataSelector,
  hasAnyCompanyCodesForManagingETRDriversSelector,
  isFetchingReportDataSelector,
  isFetchingETRDropdownsOrReportDataSelector,
} from './store/selectors';

import EffectiveTaxRateForm from './effectiveTaxRateForm.component';
import EffectiveTaxRateReport from './effectiveTaxRateReport.component';
import ReportingCurrencySelect from './reportingCurrencySelect.component';

import { EffectiveTaxRateReportPropTypes, DetailedCountryTaxTypesDataPropTypes } from './propTypes';
import { UserWithoutActiveFlagPropTypes } from '../../shared/propTypes/users';
import { VIEW_TYPES, VIEW_BY_DROPLIST_ITEMS } from './constants';
import CollapsibleWrapper from '../../shared/collapsibleWrapper/collapsibleWrapper.component';

const getStatutoryTaxRateDisplayValue = statutoryTaxRate => (
  <span>
    {statutoryTaxRate.value}
  </span>
);

class ViewEffectiveTaxRatePage extends PureComponent {
  static propTypes = {
    fetchInitialData: PropTypes.func.isRequired,
    selectGroupTypeForViewEffectiveTaxRatePage: PropTypes.func.isRequired,
    selectGroupItemForViewEffectiveTaxRatePage: PropTypes.func.isRequired,
    selectGroupBy: PropTypes.func.isRequired,
    selectViewBy: PropTypes.func.isRequired,
    selectCountryTaxType: PropTypes.func.isRequired,
    selectStatutoryTaxRate: PropTypes.func.isRequired,
    resetCountryTaxTypesAndRates: PropTypes.func.isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    groupTypes: PropTypes.arrayOf(listItemPropTypes).isRequired,
    groupType: PropTypes.string,
    slGroupItems: PropTypes.arrayOf(listItemPropTypes).isRequired,
    slGroupItem: listItemPropTypes,
    groupByOptions: PropTypes.arrayOf(listItemPropTypes).isRequired,
    groupBy: listItemPropTypes,
    viewBy: listItemPropTypes,
    isFetchingReportData: PropTypes.bool.isRequired,
    isFetchingETRDropdownsOrReportData: PropTypes.bool.isRequired,
    allCountryTaxTypes: PropTypes.arrayOf(listItemPropTypes).isRequired,
    selectedCountryTaxTypes: PropTypes.arrayOf(listItemPropTypes).isRequired,
    allStatutoryTaxRates: PropTypes.arrayOf(listItemPropTypes).isRequired,
    statutoryTaxRate: listItemPropTypes,
    reportData: EffectiveTaxRateReportPropTypes,
    detailedCountryTaxTypesData: DetailedCountryTaxTypesDataPropTypes.isRequired,
    fetchReport: PropTypes.func.isRequired,
    resetETRReport: PropTypes.func.isRequired,
    confirmDeletingDriverModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }).isRequired,
    showDeletingDriverModal: PropTypes.func.isRequired,
    user: UserWithoutActiveFlagPropTypes.isRequired,
    isInCurrentPeriod: PropTypes.bool.isRequired,
    reportingCurrencyCode: PropTypes.string.isRequired,
    hasAnyCompanyCodesForManagingETRDrivers: PropTypes.func.isRequired,
    exportToExcel: PropTypes.func.isRequired,
    toggleVisibility: PropTypes.func.isRequired,
    shouldDisplayZero: PropTypes.bool.isRequired,
    hideDecimals: PropTypes.bool.isRequired,
    negativeMinus: PropTypes.bool.isRequired,
    environmentSuffix: PropTypes.string.isRequired,
  };

  componentDidMount = () => {
    this.props.fetchInitialData({ periodName: this.props.period.name });
  };

  componentDidUpdate(prevProps) {
    if (this.props.period.name !== prevProps.period.name) {
      this.props.fetchInitialData({ periodName: this.props.period.name });
    }
  }

  componentWillUnmount = () => {
    this.props.resetCountryTaxTypesAndRates();
    this.props.resetETRReport();
  };

  onReportingCurrencyChange = () => {
    this.fetchReport();
  };

  fetchReport = selectedPeriodName => {
    const {
      fetchReport,
      period,
      groupType,
      slGroupItem,
      groupBy,
      statutoryTaxRate,
      selectedCountryTaxTypes,
      detailedCountryTaxTypesData,
    } = this.props;

    const periodName = selectedPeriodName || period.name;

    fetchReport({
      periodName,
      groupType,
      groupItem: slGroupItem && slGroupItem.value,
      groupBy: groupBy && groupBy.value,
      statutoryTaxRate,
      countryTaxTypes: selectedCountryTaxTypes,
      detailedCountryTaxTypesData,
    });
  };

  selectPeriod = selectedPeriod => {
    this.fetchReport(selectedPeriod.name);
  };

  selectGroupType = selectedGroupType => {
    const {
      period,
      selectGroupTypeForViewEffectiveTaxRatePage,
    } = this.props;
    const groupType = selectedGroupType ? selectedGroupType.value : null;
    selectGroupTypeForViewEffectiveTaxRatePage({
      period,
      groupType,
    });
  };

  selectSlGroupItem = slGroupItem => {
    const { selectGroupItemForViewEffectiveTaxRatePage } = this.props;
    selectGroupItemForViewEffectiveTaxRatePage({ groupItem: slGroupItem });
  };

  selectGroupBy = async groupBy => {
    const { selectGroupBy } = this.props;
    await selectGroupBy(groupBy);
    this.fetchReport();
  };

  selectViewBy = viewBy => {
    const { selectViewBy } = this.props;
    selectViewBy(viewBy);
  };

  exportToExcel = callback => {
    const {
      exportToExcel,
      period,
      groupType,
      slGroupItem,
      reportingCurrencyCode,
      selectedCountryTaxTypes,
      statutoryTaxRate,
      reportData,
      viewBy,
      hideDecimals,
      negativeMinus,
      shouldDisplayZero,
      environmentSuffix,
    } = this.props;

    const displayGroupingColumnsByPercentages = Boolean(viewBy && viewBy.value === VIEW_BY_DROPLIST_ITEMS.PERCENTAGES);

    exportToExcel({
      countryTaxTypes: selectedCountryTaxTypes,
      period,
      groupType,
      slGroupItem,
      reportingCurrencyCode,
      statutoryTaxRate,
      reportData,
      displayGroupingColumnsByPercentages,
      hideDecimals,
      negativeMinus,
      shouldDisplayZero,
      environmentSuffix,
    });

    if (callback) {
      callback();
    }
  };

  getSummaryText = () => {
    const summary = [
      this.getGroupTypeSummary(this.props),
      this.getGroupItemSummary(this.props),
      this.getCountryTaxTypesSummary(this.props),
      this.getTaxRateSummary(this.props),
      this.getCurrencySummary(this.props),
    ]
      .filter(item => !!item)
      .map(item => <span key={item.key}>{item.value}</span>);

    return summary.length > 0
      ? (
        <span>
          {summary.reduce((prev, curr) => [prev, ', ', curr])}
        </span>
      )
      : null;
  };

  getGroupTypeSummary = props => {
    const {
      groupTypes,
      groupType,
    } = props;

    const groupTypeObject = groupTypes.find(type => type.value === groupType);

    return groupTypeObject && { key: 1, value: groupTypeObject.label };
  };

  getGroupItemSummary = props => {
    const {
      slGroupItem,
    } = props;

    return slGroupItem && { key: 2, value: slGroupItem.label };
  };

  getCountryTaxTypesSummary = props => {
    const {
      selectedCountryTaxTypes,
    } = props;

    const countryTaxTypesText = selectedCountryTaxTypes.map(cct => cct.description).join(', ');

    return selectedCountryTaxTypes.length > 0 ? { key: 3, value: `Country Tax Types: ${countryTaxTypesText}` } : null;
  };

  getTaxRateSummary = props => {
    const {
      statutoryTaxRate,
    } = props;

    return statutoryTaxRate ? {
      key: 4,
      value: (
        <span>
          Statutory Tax Rate: {getStatutoryTaxRateDisplayValue(statutoryTaxRate)}
        </span>
      ),
    } : null;
  };

  getCurrencySummary = props => {
    const {
      reportingCurrencyCode,
    } = props;

    return {
      key: 5,
      value: `Currency: ${reportingCurrencyCode}`,
    };
  };

  render() {
    const {
      groupTypes,
      groupType,
      slGroupItems,
      slGroupItem,
      groupByOptions,
      groupBy,
      viewBy,
      isFetchingReportData,
      isFetchingETRDropdownsOrReportData,
      allCountryTaxTypes,
      selectedCountryTaxTypes,
      allStatutoryTaxRates,
      statutoryTaxRate,
      selectCountryTaxType,
      selectStatutoryTaxRate,
      period,
      reportData,
      confirmDeletingDriverModalData,
      showDeletingDriverModal,
      user,
      isInCurrentPeriod,
      reportingCurrencyCode,
      hasAnyCompanyCodesForManagingETRDrivers,
      toggleVisibility,
    } = this.props;

    const displayGroupingColumnsByPercentages = Boolean(viewBy && viewBy.value === VIEW_BY_DROPLIST_ITEMS.PERCENTAGES);

    return (
      <div className="view-tax-expense-page">
        <SecondaryMenu>
          <div className="aggregation-menu">
            <div className="aggregation-group">
              <div className="aggregation-selector">
                <PeriodDropdown
                  onChange={this.selectPeriod}
                  disabled={isFetchingETRDropdownsOrReportData}
                />
              </div>
              <div className="aggregation-selector">
                <div className="aggregation-label aggregation-label-md">Display by</div>
                <Select
                  className="select-view-types"
                  clearable={false}
                  disabled={isFetchingETRDropdownsOrReportData}
                  name="groupType"
                  options={groupTypes}
                  onChange={this.selectGroupType}
                  scrollMenuIntoView={false}
                  value={groupType}
                />
              </div>
              <div className="aggregation-selector">
                <Select
                  className="select-company-min"
                  clearable={false}
                  disabled={isFetchingETRDropdownsOrReportData}
                  options={slGroupItems}
                  value={slGroupItem}
                  onChange={this.selectSlGroupItem}
                  virtualized
                />
              </div>
              <div className="aggregation-selector">
                <div className="aggregation-label aggregation-label-md">Group by</div>
                <Select
                  className="select-view-types"
                  clearable={false}
                  disabled={isFetchingReportData}
                  name="groupBy"
                  options={groupByOptions}
                  onChange={this.selectGroupBy}
                  value={groupBy}
                  scrollMenuIntoView={false}
                  autoSelectFirstValue
                />
              </div>
              <div className="aggregation-selector">
                <ReportingCurrencySelect
                  onChange={this.onReportingCurrencyChange}
                  disabled={isFetchingReportData}
                />
              </div>
            </div>
            { groupBy && groupBy.value
              ? (<Select
                className="view-by-dropdown select-view-types-min"
                clearable={false}
                name="viewType"
                options={VIEW_TYPES}
                onChange={this.selectViewBy}
                value={viewBy}
                scrollMenuIntoView={false}
                autoSelectFirstValue
              />) : null
            }
            {this.exportToExcel
              ? (
                <div className="hamburger-dropdown ml-auto text-right">
                  <HamburgerDropdown
                    exportToExcel={this.exportToExcel}
                    disableExportToExcel={Boolean(!reportData || isFetchingReportData)}
                  />
                </div>
              )
              : null
            }
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid page-content-container">
            <div className="row no-gutters">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Effective Tax Rate (ETR)</h5>
                </div>
                <CollapsibleWrapper className="chosenValuesText">
                  { this.getSummaryText() }
                </CollapsibleWrapper>
                <EffectiveTaxRateForm
                  allCountryTaxTypes={allCountryTaxTypes}
                  selectedCountryTaxTypes={selectedCountryTaxTypes}
                  allStatutoryTaxRates={allStatutoryTaxRates}
                  statutoryTaxRate={statutoryTaxRate}
                  selectCountryTaxType={selectCountryTaxType}
                  selectStatutoryTaxRate={selectStatutoryTaxRate}
                  groupType={groupType}
                  groupItem={slGroupItem && slGroupItem.value}
                  groupBy={groupBy && groupBy.value}
                  periodName={period.name}
                />
              </div>
            </div>
            <div className="row no-gutters">
              <div className="col">
                {<EffectiveTaxRateReport
                  reportData={reportData}
                  fetchReport={this.fetchReport}
                  confirmDeletingDriverModalData={confirmDeletingDriverModalData}
                  showDeletingDriverModal={showDeletingDriverModal}
                  user={user}
                  hideGroupingColumns={!(groupBy && groupBy.value)}
                  isInCurrentPeriod={isInCurrentPeriod}
                  reportingCurrencyCode={reportingCurrencyCode}
                  suppressHidingDecimalPlacesForPercentage
                  isLoading={isFetchingETRDropdownsOrReportData}
                  groupingColumns={reportData && reportData.columnHeaders}
                  hasAnyCompanyCodesForManagingETRDrivers={hasAnyCompanyCodesForManagingETRDrivers}
                  displayGroupingColumnsByPercentages={displayGroupingColumnsByPercentages}
                  toggleVisibility={toggleVisibility}
                />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    period: periodOrCurrentSelector(state),
    groupTypes: selectSlGroupTypes(state),
    groupType: selectSlGroupType(state),
    slGroupItems: selectSlGroupItems(state),
    slGroupItem: selectSlGroupItem(state),
    groupByOptions: groupByOptionsSelector(state),
    groupBy: groupBySelector(state),
    viewBy: viewBySelector(state),
    isFetchingReportData: isFetchingReportDataSelector(state),
    isFetchingETRDropdownsOrReportData: isFetchingETRDropdownsOrReportDataSelector(state),
    selectedCountryTaxTypes: state.reports.effectiveTaxRate.selectedCountryTaxTypes,
    allCountryTaxTypes: countryTaxTypesSelector(state),
    allStatutoryTaxRates: countryTaxTypesStatutoryTaxRatesSelector(state),
    statutoryTaxRate: state.reports.effectiveTaxRate.statutoryTaxRate,
    reportData: reportDataSelector(state),
    reportingCurrencyCode: reportingCurrencyCodeSelector(state),
    detailedCountryTaxTypesData: detailedCountryTaxTypesDataSelector(state),
    confirmDeletingDriverModalData: modalDataSelector(state, ModalNames.ConfirmDeletingDriver),
    user: userSelector(state),
    isInCurrentPeriod: isInCurrentPeriodSelector(state),
    hasAnyCompanyCodesForManagingETRDrivers: hasAnyCompanyCodesForManagingETRDriversSelector(state),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialData,
    selectGroupTypeForViewEffectiveTaxRatePage,
    selectGroupItemForViewEffectiveTaxRatePage,
    selectGroupBy,
    selectViewBy,
    selectCountryTaxType,
    selectStatutoryTaxRate,
    resetCountryTaxTypesAndRates,
    fetchReport,
    resetETRReport,
    showDeletingDriverModal,
    hideDeletingDriverModal,
    deleteCustomETRDriver,
    exportToExcel,
    toggleVisibility,
  }
)(ViewEffectiveTaxRatePage);
